// Theme + font selection. Writes data-theme / data-font on <html> (tokens.css
// does the rest) and fires the same window events the existing app listens for.

import { useCallback, useEffect, useState } from "react";

export const THEMES = [
  { id: "light", label: "Modernist", swatches: ["#f3f2f2", "#201e1d", "#ec3013"], note: "Ink on paper, one red" },
  { id: "dark", label: "Ink Dark", swatches: ["#171514", "#cfc8c5", "#ff4a26"], note: "Same grid, night ground" },
  { id: "ocean", label: "Ocean", swatches: ["#0d1b22", "#c4d7dd", "#4cc9d0"], note: "Deep teal, cyan accent" },
];

export const FONTS = [
  { id: "segoe", label: "Segoe UI" },
  { id: "montserrat", label: "Montserrat" },
];

const THEME_KEY = "hoa-theme";
const FONT_KEY = "hoa-font";
const DEBUG_KEY = "hoa-chat-debug-console";

const read = (key, fallback) => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};
const write = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
};

export function applyTheme(themeId) {
  document.documentElement.setAttribute("data-theme", themeId);
  write(THEME_KEY, themeId);
  window.dispatchEvent(new CustomEvent("hoa:theme-change", { detail: themeId }));
}

export function applyFont(fontId) {
  document.documentElement.setAttribute("data-font", fontId);
  write(FONT_KEY, fontId);
  window.dispatchEvent(new CustomEvent("hoa:font-change", { detail: fontId }));
}

/** Reads persisted choices on mount and keeps <html> in sync. */
export function useTheme(defaultTheme = "light") {
  const [theme, setThemeState] = useState(() => read(THEME_KEY, defaultTheme));
  const [font, setFontState] = useState(() => read(FONT_KEY, "segoe"));

  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => { applyFont(font); }, [font]);

  useEffect(() => {
    const onTheme = (e) => { if (e.detail && e.detail !== theme) setThemeState(e.detail); };
    window.addEventListener("hoa:theme-change", onTheme);
    return () => window.removeEventListener("hoa:theme-change", onTheme);
  }, [theme]);

  return {
    theme,
    font,
    setTheme: useCallback((id) => setThemeState(id), []),
    setFont: useCallback((id) => setFontState(id), []),
  };
}

/** Settings' debug-console switch, broadcast to every mounted chat surface. */
export function useDebugConsole() {
  const [on, setOn] = useState(() => read(DEBUG_KEY, "0") === "1");

  useEffect(() => {
    const listener = (e) => setOn(!!e.detail);
    window.addEventListener("hoa:chat-debug-console-change", listener);
    return () => window.removeEventListener("hoa:chat-debug-console-change", listener);
  }, []);

  const toggle = useCallback(() => {
    setOn((prev) => {
      const next = !prev;
      write(DEBUG_KEY, next ? "1" : "0");
      window.dispatchEvent(new CustomEvent("hoa:chat-debug-console-change", { detail: next }));
      return next;
    });
  }, []);

  return { debugOn: on, toggleDebug: toggle };
}
